import type {SeriesOptionsType} from 'highcharts';
import CubeIndex from '../cube/CubeIndex';
import {itemColor} from '../render/colors';
import {escapeHtml, formatValue, tooltipHtml} from '../render/format';
import {isLowBase, lowBaseAttributes} from '../render/lowBase';
import {MarkRenderer} from '../types';

const renderPie: MarkRenderer = (cube, spec, ctx) => {
    const index = new CubeIndex(cube);
    const channel = typeof spec.encoding.slice === 'string' ? spec.encoding.slice : cube.dimensions[0]?.id;
    const measure = cube.measures[0];

    // The total member would double count the slices
    const data = index.items(channel).filter(item => item.member?.role !== 'total').flatMap((item, itemIndex) => {
        // on a $measures axis every slice is its own measure
        const sliceMeasure = item.measure ?? measure;
        const cell = index.value(item.measure ? {} : {[channel]: item.index}, sliceMeasure.id);
        if (cell?.value === null || cell?.value === undefined) {
            return [];
        }
        // item.measure is set only when every slice is its own measure; otherwise one shared measure colour would be meaningless.
        const color = itemColor(item.member, item.measure, itemIndex, ctx);
        const lowBase = isLowBase(cube, cell);

        return [{
            id: item.id,
            name: escapeHtml(item.label),
            y: cell.value,
            color,
            ...(lowBase ? lowBaseAttributes('pie', color, ctx) : {}),
            custom: {
                count: cell.count,
                lowBase,
                valueText: formatValue(cell.value, sliceMeasure, ctx),
                tooltip: tooltipHtml({heading: [item.label], color, measure: sliceMeasure, cell, lowBase}, ctx),
            },
        }];
    });

    return {
        type: 'highcharts',
        options: {
            chart: {type: 'pie'},
            title: {text: undefined},
            credits: {enabled: false},
            legend: {enabled: true},
            tooltip: {headerFormat: '', pointFormat: '{point.custom.tooltip}'},
            plotOptions: {
                // dataLabels live on `series` so the shared data-label toggle can override them
                series: {
                    dataLabels: {enabled: spec.options.dataLabels !== false, format: '{point.name}: {point.custom.valueText}'},
                },
                pie: {innerSize: spec.options.donut === true ? '50%' : '0%', showInLegend: true},
            },
            series: [{type: 'pie', id: measure.id, name: escapeHtml(measure.label), data} as SeriesOptionsType],
        },
    };
};

export default renderPie;
